import { Clock, Mail, MapPin, MessageCircle, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SITE } from "@/data/site";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(SITE.address.mapsQuery)}&output=embed&hl=pt-BR`;
const mapsLinkUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.address.mapsQuery)}`;

export const Contact = () => {
  return (
    <section id="contato" data-testid="contact-section" className="section-padding scroll-mt-24 bg-background">
      <div className="container-site flex flex-col gap-14">
        <Reveal>
          <SectionHeading
            id="contact"
            eyebrow="Contato e localização"
            title="Estamos à sua espera."
            description="Venha nos conhecer ou fale com a equipe pelos canais abaixo."
          />
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden rounded-[var(--radius)] border border-border shadow-[0_10px_30px_rgba(20,16,12,0.06)]">
              <iframe
                title="Localização da RAMALIVE ODONTOLOGIA no Google Maps"
                src={mapsEmbedUrl}
                className="h-[320px] w-full sm:h-[420px] lg:h-full lg:min-h-[460px]"
                style={{ border: 0, filter: "saturate(0.85) sepia(0.08)" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
              />
            </div>
          </Reveal>

          <div className="flex flex-col gap-6 lg:col-span-5">
            <Reveal delay={0.05}>
              <div className="card-premium flex flex-col gap-5 p-6" data-testid="contact-info-card">
                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground">Endereço</h3>
                    <p className="mt-1 text-sm text-muted-foreground" data-testid="contact-address">
                      {SITE.address.line1}
                      <br />
                      {SITE.address.line2}
                    </p>
                    <a
                      href={mapsLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-directions-link"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-[hsl(var(--brand-soft-brown))] transition-colors duration-200 hover:text-[hsl(var(--brand-champagne-gold))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Navigation className="h-4 w-4" />
                      Como chegar
                    </a>
                  </div>
                </div>

                <Separator />

                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground">WhatsApp</h3>
                    <a
                      href={buildWhatsAppUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="mt-1 inline-block rounded-md text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {SITE.whatsappDisplay}
                    </a>
                  </div>
                </div>

                <Separator />

                <div className="flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground">E-mail</h3>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email-link"
                      className="mt-1 inline-block rounded-md text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="card-premium flex flex-col gap-4 p-6" data-testid="contact-hours-card">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
                    <Clock className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">Horário de funcionamento</h3>
                </div>
                <ul className="flex flex-col">
                  {SITE.hours.map((h, i) => (
                    <li key={h.day}>
                      <div className="flex items-center justify-between py-3 text-sm">
                        <span className="text-muted-foreground">{h.day}</span>
                        <span className="font-semibold text-foreground">{h.time}</span>
                      </div>
                      {i < SITE.hours.length - 1 && <Separator />}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="mt-2 h-12 w-full rounded-xl active:scale-[0.98] transition-[background-color,transform] duration-200"
                >
                  <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Falar com a equipe
                  </a>
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
